import type { Module } from "vuex";

import type { Editable, EditableWithOverwrites, Font, Overwrite } from "hoxton-types";

import type { Asset, EditedEditable } from "@/types/types";

import { dedup, formatEditableName } from "@/utils";
import { EditableScope } from "@/enums/editables";
import * as editableUtils from "@/utils/editables";
import { campaignDefaultLanguage, CampaignGetters } from "@/store/modules/campaign";
import { MEDIA_TYPES } from "@/enums/mediaLibrary";
import type { LanguageCode } from "@/utils/languages";

import { type RootState } from "@/store/types";

const storeNamespace = "editor";

export const EditorMutation = {
    AddGroupSelection: "addGroupSelection",
    ClearEditingGroupName: "clearEditingGroupName",
    ClearLocalOverwritesForAsset: "clearLocalOverwritesForAsset",
    ClearLocalOverwritesForGroupValue: "clearLocalOverwritesForGroupValue",
    ClearLocalOverwritesForLanguage: "clearLocalOverwritesForLanguage",
    ClearSelectedGroupValues: "clearSelectedGroupValues",
    ClearRemovedOverwrites: "clearRemovedOverwrites",
    RemoveOverwrite: "removeOverwrite",
    SetEditedEditable: "setEditedEditable",
    SetFonts: "setFonts",
    SetLocalOverwriteTmpId: "setLocalOverwriteTmpId",
    SelectLanguage: "selectLanguage",
    SetEditablesPaneVisibility: "setEditablesPaneVisibility",
    SetSettingsPaneVisibility: "setSettingsPaneVisibility",
    SetShowEditablesPaneInModal: "setShowEditablesPaneInModal",
    SetShowSettingsPaneInModal: "setShowSettingsPaneInModal",
    SetLocalEditableOverwrites: "setLocalEditableOverwrites",
    UpdateSelectedGroupValues: "updateSelectedGroupValues",
    UpdateBeforeUnloadListener: "updateBeforeUnloadListener",
    SetIsSandboxMode: "setIsSandboxMode"
};

export const EditorAction = {
    ClearGroupValueData: `${storeNamespace}/clearGroupValueData`,
    ClearLanguageData: `${storeNamespace}/clearLanguageData`,
    ClearLocalAssetChanges: `${storeNamespace}/clearAssetData`,
    ClearLocalChanges: `${storeNamespace}/clearLocalChanges`,
    ClearSelectedGroupValues: `${storeNamespace}/clearSelectedGroupValues`,
    RemoveOverwrite: `${storeNamespace}/removeOverwrite`,
    ResetEditor: `${storeNamespace}/resetEditor`,
    SetEditedEditable: `${storeNamespace}/setEditedEditable`,
    SetFonts: `${storeNamespace}/setFonts`,
    SetLocalOverwriteTmpId: `${storeNamespace}/setLocalOverwriteTmpId`,
    SelectGroupValue: `${storeNamespace}/selectGroupValue`,
    SelectLanguage: `${storeNamespace}/selectLanguage`,
    SetLocalEditableOverwrites: `${storeNamespace}/setLocalEditableOverwrites`,
    SetEditablesPaneVisibility: `${storeNamespace}/setEditablesPaneVisibility`,
    SetSettingsPaneVisibility: `${storeNamespace}/setSettingsPaneVisibility`,
    SetShowEditablesPaneInModal: `${storeNamespace}/setShowEditablesPaneInModal`,
    SetShowSettingsPaneInModal: `${storeNamespace}/setShowSettingsPaneInModal`,
    SetIsSandboxMode: `${storeNamespace}/setIsSandboxMode`
};

export const EditorGetters = {
    editableOverwrites: `${storeNamespace}/editableOverwrites`,
    editableOverwriteValue: `${storeNamespace}/editableOverwriteValue`,
    fontIds: `${storeNamespace}/fontIds`,
    fontsByName: `${storeNamespace}/fontsByName`,
    groupsForSelectedEditable: `${storeNamespace}/groupsForSelectedEditable`,
    selectedEditables: `${storeNamespace}/selectedEditables`,
    selectedGroupValuesIds: `${storeNamespace}/selectedGroupValuesIds`,
    editingGroupValue: `${storeNamespace}/editingGroupValue`
};

const beforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    return "You will lose all content edits made since your last save.";
};

export type State = {
    allowMultiGroupValues: boolean;
    editingGroupName: string | null;
    editedEditable: EditedEditable | null;
    hasBeforeUnloadListener: boolean;
    isEditablesPaneVisible: boolean;
    isSettingsPaneVisible: boolean;
    localEditableOverwrites: EditableWithOverwrites[];
    localOverwrites: unknown[]; // is it used?!
    localOverwriteTmpId: number;
    removedOverwriteIds: string[];
    selectedGroupValues: Record<string, { groupName: string; groupValueId: string }>;
    selectedGroupValueIds: string[];
    selectedLanguage: LanguageCode;
    showSettingsPaneInModal: boolean;
    showEditablesPaneInModal: boolean;
    fonts: Record<string, Font>;
    isSandboxMode: boolean;
};

export type Getters = {
    editableOverwrites: () => EditableWithOverwrites[];
    editableOverwriteValue: () => Overwrite;
    fontsByName: () => Record<string, Font>;
    fontIds: () => string[];
    groupsForSelectedEditable: () => Record<string, string[]>;
    selectedEditables: () => Editable[];
    selectedGroupValuesIds: () => string[];
    editingGroupValue: () => State["selectedGroupValues"][string] | undefined;
};

const getDefaultState = (): State => ({
    allowMultiGroupValues: true,
    editingGroupName: null,
    editedEditable: null,
    hasBeforeUnloadListener: false,
    isSettingsPaneVisible: false,
    isEditablesPaneVisible: false,
    localEditableOverwrites: [],
    localOverwrites: [],
    localOverwriteTmpId: 1,
    removedOverwriteIds: [],
    selectedGroupValues: {},
    selectedGroupValueIds: [],
    selectedLanguage: campaignDefaultLanguage,
    showSettingsPaneInModal: false,
    showEditablesPaneInModal: false,
    fonts: {},
    isSandboxMode: false
});

const editorModule: Module<State, RootState> = {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        editableOverwrites: (state, getters, rootState) => {
            if (!state.removedOverwriteIds.length) {
                return editableUtils.mergeEditables(
                    rootState.campaign.editableOverwrites,
                    state.localEditableOverwrites
                );
            }

            const dbEditableOverwrites = rootState.campaign.editableOverwrites
                // exclude removed overwrites
                .reduce<EditableWithOverwrites[]>((acc, editableOverwrite) => {
                    const overwrites = editableOverwrite.overwrites.filter(
                        overwrite => !state.removedOverwriteIds.includes(overwrite._id)
                    );

                    if (overwrites.length) {
                        acc.push({
                            ...editableOverwrite,
                            overwrites
                        });
                    }
                    return acc;
                }, []);

            return editableUtils.mergeEditables(dbEditableOverwrites, state.localEditableOverwrites);
        },

        editableOverwriteValue:
            (state, getters: Getters, rootState, rootGetters) =>
            (editableId: string, masterTemplateId: string, groupValuesIds = getters.selectedGroupValuesIds) =>
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                rootGetters[CampaignGetters.editableOverwriteValue](
                    editableId,
                    masterTemplateId,
                    state.selectedLanguage,
                    groupValuesIds,
                    getters.editableOverwrites
                ) as Overwrite,

        fontsByName(state) {
            return Object.values(state.fonts).reduce<Record<string, Font>>((acc, font) => {
                acc[font.name] = font;
                return acc;
            }, {});
        },

        fontIds(state) {
            return Object.keys(state.fonts);
        },

        // this could be wrong for multi-group
        groupsForSelectedEditable: (state, getters: Getters) => {
            const selectedGroup = state.editingGroupName;

            if (!selectedGroup) {
                return {};
            }

            const selectedEditables = getters.selectedEditables as unknown as Editable[];

            return selectedEditables.reduce<Record<string, string[]>>((acc, editable) => {
                const defaultValues = editable.defaultValues.filter(({ editableGroups }) =>
                    editableGroups?.includes(selectedGroup)
                );
                if (defaultValues.length) {
                    acc[editable._id] = defaultValues[0]?.editableGroups ?? [];
                }

                return acc;
            }, {});
        },

        selectedEditables: (state, getters, rootState) => {
            if (!state.editingGroupName) {
                return [];
            }

            const selectedGroup = rootState.campaign.normalized.editableGroups[state.editingGroupName];

            const selectedEditables = selectedGroup?.editables.reduce<
                (Editable & { label: string; scope: typeof EditableScope.Group })[]
            >((acc, selectedGroupEditableId) => {
                const editable = rootState.campaign.normalized.editables[selectedGroupEditableId];
                if (editable) {
                    acc.push({
                        ...editable,
                        label: formatEditableName(editable.name),
                        scope: EditableScope.Group
                    });
                }
                return acc;
            }, []);

            const uniqueEditables = dedup(selectedEditables ?? [], "_id");

            return uniqueEditables;
        },

        selectedGroupValuesIds: state => state.selectedGroupValueIds, // todo: to be removed

        editingGroupValue: state =>
            state.editingGroupName !== null ? state.selectedGroupValues[state.editingGroupName] : undefined
    },

    mutations: {
        addGroupSelection(state, groupValue: State["selectedGroupValues"][string]) {
            // line below left as a reference - if we need to disallow multigroup selection again this is the way to do it
            // const val = state.allowMultiGroupValues ? deepClone( state.selectedGroupValues ) : {};

            state.selectedGroupValues[groupValue.groupName] = groupValue;

            state.editingGroupName = groupValue.groupName;
            state.selectedGroupValueIds = state.selectedGroupValueIds.concat(groupValue.groupValueId);
        },

        clearEditingGroupName(state) {
            state.editingGroupName = null;
        },

        clearLocalOverwritesForAsset(state, removedAsset: Asset) {
            state.localEditableOverwrites = state.localEditableOverwrites
                // remove local overwrites in the removed language
                .map(editableOverwrite => ({
                    ...editableOverwrite,
                    overwrites: editableOverwrite.overwrites.filter(overwrite => {
                        const isIdMatched = overwrite.mediaItem && overwrite.mediaItem.id === removedAsset.id;

                        if (removedAsset.type === MEDIA_TYPES.FOLDER) {
                            return (
                                !isIdMatched && !overwrite.mediaItem?.persistentKey.includes(removedAsset.persistentKey)
                            );
                        }

                        return !isIdMatched;
                    })
                }));
        },

        clearLocalOverwritesForGroupValue(state, removedGroupValueId: State["selectedGroupValueIds"][number]) {
            state.localEditableOverwrites = state.localEditableOverwrites
                // remove local overwrites in the removed language
                .map(editableOverwrite => ({
                    ...editableOverwrite,
                    overwrites: editableOverwrite.overwrites.filter(
                        overwrite => !overwrite.editableGroupValueIds.includes(removedGroupValueId)
                    )
                }))
                // remove editableOverwrite if we removed all the overwrites in the previous step
                .filter(editableOverwrite => editableOverwrite.overwrites.length);
        },

        clearLocalOverwritesForLanguage(state, language: State["selectedLanguage"]) {
            state.localEditableOverwrites = state.localEditableOverwrites
                // remove local overwrites in the removed language
                .map(editableOverwrite => ({
                    ...editableOverwrite,
                    overwrites: editableOverwrite.overwrites.filter(overwrite => overwrite.language !== language)
                }))
                // remove editableOverwrite if we removed all the overwrites in the previous step
                .filter(editableOverwrite => editableOverwrite.overwrites.length);
        },

        clearSelectedGroupValues(state) {
            state.selectedGroupValues = {};
        },

        clearRemovedOverwrites(state) {
            state.removedOverwriteIds = [];
        },

        removeOverwrite(state, overwriteId: string) {
            if (overwriteId.includes("tmpId-")) {
                // Remove local overwrite
                state.localEditableOverwrites = state.localEditableOverwrites
                    .map(editableOverwrite => ({
                        ...editableOverwrite,
                        overwrites: editableOverwrite.overwrites.filter(overwrite => overwrite._id !== overwriteId)
                    }))
                    // remove editableOverwrite if we removed all the overwrites in the previous step
                    .filter(editableOverwrite => editableOverwrite.overwrites.length);
            } else {
                state.removedOverwriteIds = state.removedOverwriteIds.concat(overwriteId);
            }
        },

        setLocalEditableOverwrites(state, editableOverwrites: State["localEditableOverwrites"]) {
            state.localEditableOverwrites = editableOverwrites;
        },

        setEditedEditable(state, data: State["editedEditable"]) {
            state.editedEditable = data;
        },

        setFonts(state, fonts: Font[]) {
            state.fonts = {};
            fonts.forEach(font => {
                state.fonts[font._id] = font;
            });
        },

        setLocalOverwriteTmpId(state, tmpId: State["localOverwriteTmpId"]) {
            state.localOverwriteTmpId = tmpId;
        },

        selectLanguage(state, language: State["selectedLanguage"]) {
            state.selectedLanguage = language;
        },

        setEditablesPaneVisibility(state, isEditablesPaneVisible: State["isEditablesPaneVisible"]) {
            state.isEditablesPaneVisible = isEditablesPaneVisible;
        },

        setSettingsPaneVisibility(state, isSettingsPaneVisible: State["isSettingsPaneVisible"]) {
            state.isSettingsPaneVisible = isSettingsPaneVisible;
        },

        setShowEditablesPaneInModal(state, showEditablesPaneInModal: State["showEditablesPaneInModal"]) {
            state.showEditablesPaneInModal = showEditablesPaneInModal;
        },
        setShowSettingsPaneInModal(state, showSettingsPaneInModal: State["showSettingsPaneInModal"]) {
            state.showSettingsPaneInModal = showSettingsPaneInModal;
        },

        setIsSandboxMode(state, isSandboxMode: State["isSandboxMode"]) {
            state.isSandboxMode = isSandboxMode;
        },

        updateBeforeUnloadListener(state) {
            if (
                !state.hasBeforeUnloadListener &&
                (state.removedOverwriteIds.length || state.localEditableOverwrites.length)
            ) {
                window.addEventListener("beforeunload", beforeUnload);
                state.hasBeforeUnloadListener = true;
            }

            if (
                state.hasBeforeUnloadListener &&
                !state.removedOverwriteIds.length &&
                !state.localEditableOverwrites.length
            ) {
                window.removeEventListener("beforeunload", beforeUnload);
                state.hasBeforeUnloadListener = true;
            }
        },

        updateSelectedGroupValues(state, groupValue: State["selectedGroupValues"][string]) {
            const previousEditableGroupValue = state.selectedGroupValues[groupValue.groupName];

            state.selectedGroupValues[groupValue.groupName] = groupValue;
            state.editedEditable = null;

            if (previousEditableGroupValue) {
                const previousEditableGroupValueId = previousEditableGroupValue.groupValueId;
                const previousEditableGroupValueIdIndex =
                    state.selectedGroupValueIds.indexOf(previousEditableGroupValueId);
                const index =
                    previousEditableGroupValueIdIndex > -1
                        ? previousEditableGroupValueIdIndex
                        : state.selectedGroupValueIds.length;

                state.selectedGroupValueIds[index] = groupValue.groupValueId;
            }

            state.editingGroupName = groupValue.groupName;
        }
    },

    actions: {
        clearAssetData({ commit }, removedAsset) {
            commit(EditorMutation.ClearLocalOverwritesForAsset, removedAsset);
        },

        clearGroupValueData({ commit, state }, removedGroupValueId) {
            commit(EditorMutation.ClearLocalOverwritesForGroupValue, removedGroupValueId);

            if (state.editingGroupName) {
                const selectedGroup = state.selectedGroupValues[state.editingGroupName];

                // !selectedGroup.groupValueId cause at this stage the value has already been removed.
                if (selectedGroup && !selectedGroup.groupValueId) {
                    commit(EditorMutation.ClearEditingGroupName);
                }
            }
        },

        clearLanguageData({ commit, state }, removedLanguage: State["selectedLanguage"]) {
            commit(EditorMutation.ClearLocalOverwritesForLanguage, removedLanguage);

            if (state.selectedLanguage === removedLanguage) {
                commit(EditorMutation.SelectLanguage, campaignDefaultLanguage);
            }
        },

        clearLocalChanges({ commit }) {
            commit(EditorMutation.SetLocalEditableOverwrites, []);
            commit(EditorMutation.ClearRemovedOverwrites);
            commit(EditorMutation.UpdateBeforeUnloadListener);
        },

        clearSelectedGroupValues({ commit }) {
            commit(EditorMutation.ClearSelectedGroupValues);
        },

        removeOverwrite({ commit }, payload) {
            commit(EditorMutation.RemoveOverwrite, payload);
            commit(EditorMutation.UpdateBeforeUnloadListener);
        },

        resetEditor({ commit }) {
            commit(EditorMutation.ClearSelectedGroupValues);
            commit(EditorMutation.ClearEditingGroupName);
        },

        setLocalEditableOverwrites({ commit }, editableOverwrites: State["localEditableOverwrites"]) {
            commit(EditorMutation.SetLocalEditableOverwrites, editableOverwrites);
            commit(EditorMutation.UpdateBeforeUnloadListener);
        },

        selectGroupValue({ commit, state }, payload: State["selectedGroupValues"][string]) {
            let mutation: typeof EditorMutation.AddGroupSelection = EditorMutation.AddGroupSelection;

            if (payload.groupName in state.selectedGroupValues) {
                mutation = EditorMutation.UpdateSelectedGroupValues;
            }

            commit(mutation, payload);
        },

        setEditedEditable({ commit }, payload: State["editedEditable"]) {
            commit(EditorMutation.SetEditedEditable, payload);

            if (!payload) {
                commit(EditorMutation.SetSettingsPaneVisibility, false);
            }
        },

        setFonts({ commit }, payload: Font[]) {
            commit(EditorMutation.SetFonts, payload);
        },

        setLocalOverwriteTmpId({ commit }, payload: State["localOverwriteTmpId"]) {
            commit(EditorMutation.SetLocalOverwriteTmpId, payload);
        },

        selectLanguage({ commit }, payload: State["selectedLanguage"]) {
            commit(EditorMutation.SelectLanguage, payload);
        },

        setSettingsPaneVisibility({ commit }, isSettingsPaneVisible: State["isSettingsPaneVisible"]) {
            commit(EditorMutation.SetSettingsPaneVisibility, isSettingsPaneVisible);
        },

        setEditablesPaneVisibility({ commit }, isEditablesPaneVisible: State["isEditablesPaneVisible"]) {
            commit(EditorMutation.SetEditablesPaneVisibility, isEditablesPaneVisible);
        },

        setShowEditablesPaneInModal({ commit }, showEditablesPaneInModal: State["showEditablesPaneInModal"]) {
            commit(EditorMutation.SetShowEditablesPaneInModal, showEditablesPaneInModal);
        },

        setShowSettingsPaneInModal({ commit }, showSettingsPaneInModal: State["showSettingsPaneInModal"]) {
            commit(EditorMutation.SetShowSettingsPaneInModal, showSettingsPaneInModal);
        },

        setIsSandboxMode({ commit }, isSandboxMode: State["isSandboxMode"]) {
            commit(EditorMutation.SetIsSandboxMode, isSandboxMode);
        }
    }
};

export default editorModule;
